import React, { ReactNode } from 'react';
import styles from '../CreateProductFlow.module.scss';
import {
  ButtonLabels,
  FieldProps,
  RawMaterialProps,
} from 'enums/RawMaterialEnums';
import { ReactComponent as AddIcon } from 'assets/icons/add_enclosed.svg';
import clsx from 'clsx';
import IngridientNameField from '../IngridientNameField';
import InputField from 'shared/components/InputField';
import { ReactComponent as DeleteButton } from 'assets/icons/close_enclosed.svg';
import { ReactComponent as MapSearch } from 'assets/icons/map-search.svg';
import BestFitRegion from '../BestFitRegion';
import COSelect from 'shared/components/COSelect';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrayHelpers, FormikProps, useFormikContext } from 'formik';
import {
  Origin,
  rawMaterialPayload,
} from 'views/CreateProductFlow/CreateProductFlowInterfaces';
import { RawMaterialIngredientsMeta } from 'models/RawMaterialIngredientsMeta/rawMaterialIngredientsMeta.model';
import { unitOptions } from '../CreateProductFlowConstants';
import { Product } from 'models/Product/product.model';
import hasErrorAndTouched from 'shared/utils/hasErrorAndTouched';
import { validateForHandleSave } from './validateForHandleSave';
import { Tooltip, Typography } from 'antd';
import IngredientOriginFields from './IngredientOriginFields';
import AddOriginButton from './AddOriginButton';
import IngredientHeader from './IngredientHeader';
import {
  handleIngredientOnChange,
  onOriginClick,
  ingredientMenu,
  fieldDisabled,
} from './IngredientListUtils';

interface IngredientListInterface {
  isDisabled: boolean;
  arrayHelpers: ArrayHelpers;
  buttonLoading: boolean;
  handleOpenCancelModal: () => void;
  draftButtonLoading: boolean;
  handleSaveAsDraft: (payload: rawMaterialPayload) => void;
  rawMaterialIngredientsMeta: RawMaterialIngredientsMeta[];
  handleOpenRequestIngredientModal: () => void;
  fetchData: (params?: { 'input-field': string }) => void;
  showLocationModal: (
    index: number,
    type: string,
    selectedIndexData?: Origin[],
    selectedRecordIndex?: number,
    selectedRecordType?: string,
    selectedOriginIndex?: number
  ) => void;
  newProduct?: Product;
  handleAddOrigins: (
    formikProps: FormikProps<rawMaterialPayload>,
    index: number,
    type?: string,
    id?: string
  ) => void;
  handleRemoveOrigins: (
    formikProps: FormikProps<rawMaterialPayload>,
    index: number,
    originIndex: number,
    type?: string
  ) => void;
}

const IngredientList: FC<IngredientListInterface> = props => {
  const navigate = useNavigate();
  const handleGoBack = () => navigate(-1);

  const {
    isDisabled,
    arrayHelpers,
    buttonLoading,
    handleOpenCancelModal,
    draftButtonLoading,
    handleSaveAsDraft,
    rawMaterialIngredientsMeta,
    handleOpenRequestIngredientModal,
    fetchData,
    showLocationModal,
    newProduct,
    handleAddOrigins,
    handleRemoveOrigins,
  } = props;

  const formikProps = useFormikContext<rawMaterialPayload>();

  const handleTopBackButton = () => navigate(-1);

  const handleIngredientOnChangeWrapper = (value: string, index: number) => {
    handleIngredientOnChange(value, index, formikProps);
  };

  const onOriginClickWrapper = (index: number) => {
    onOriginClick(index, isDisabled, formikProps, showLocationModal);
  };

  const ingredientMenuWrapper = (menu: ReactNode) =>
    ingredientMenu(menu, handleOpenRequestIngredientModal, styles);

  const fieldDisabledWrapper = (index: number) =>
    fieldDisabled(index, isDisabled, formikProps);

  return (
    <div>
      <IngredientHeader
        isDisabled={isDisabled}
        buttonLoading={buttonLoading}
        draftButtonLoading={draftButtonLoading}
        handleGoBack={handleGoBack}
        handleTopBackButton={handleTopBackButton}
        handleOpenCancelModal={handleOpenCancelModal}
        handleSaveAsDraft={() =>
          validateForHandleSave(formikProps, handleSaveAsDraft)
        }
        arrayHelpers={arrayHelpers}
        formikProps={formikProps}
      />
      <div className={styles.ingredientsFormContainer}>
        <h2>{RawMaterialProps.INGREDIENT_HEADING}</h2>
        {formikProps.values.ingredients.map((_, index: number) => (
          <React.Fragment key={`${RawMaterialProps.INGREDIENTS}-${index}`}>
            <div className={styles.fieldRow}>
              <div>
                <p className={styles.pointer}>{index + 1}</p>
              </div>
              <div className={styles.ingredientFieldContainer}>
                <IngridientNameField
                  label={FieldProps.INGREDIENT_NAME_PLACEHOLDER}
                  placeholder={FieldProps.INGREDIENT_NAME_PLACEHOLDER}
                  onChange={value =>
                    handleIngredientOnChangeWrapper(value, index)
                  }
                  options={rawMaterialIngredientsMeta}
                  dropdownRender={menu => ingredientMenuWrapper(menu)}
                  disabled={isDisabled ? true : false}
                  onFocus={() => {
                    fetchData({
                      'input-field': 'ingredient name',
                    });
                  }}
                  name={`${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_NAME}`}
                  bestFitRegionName={`${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_REGION}`}
                  ingredientOriginName={`${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_ORIGIN}`}
                  addressName={`${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.ADDRESS}`}
                />
              </div>
              <div className={styles.bigFieldContainer}>
                <InputField
                  name={`${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_ORIGIN}`}
                  label={FieldProps.INGREDIENT_ORIGIN_PLACEHOLDER}
                  placeholderstring={FieldProps.INGREDIENT_ORIGIN_PLACEHOLDER}
                  disabled={fieldDisabledWrapper(index)}
                  suffix={
                    <span onClick={() => onOriginClickWrapper(index)}>
                      <MapSearch />
                    </span>
                  }
                  onFocus={() => onOriginClickWrapper(index)}
                />
              </div>
              <div className={styles.bigFieldContainer}>
                <BestFitRegion
                  label={FieldProps.INGREDIENT_REGION_PLACEHOLDER}
                  placeholder={FieldProps.INGREDIENT_REGION_PLACEHOLDER}
                  disabled={fieldDisabledWrapper(index)}
                  onFocus={() =>
                    fetchData({
                      'input-field': 'production region',
                    })
                  }
                  name={`${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_REGION}`}
                  ingridientFieldName={`${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_NAME}`}
                  errorMessageStyle={styles.errorMessage}
                />
              </div>
              <div className={styles.smallFieldContainer}>
                <InputField
                  key={FieldProps.INGREDIENT_QUANTITY}
                  name={`${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_QUANTITY}`}
                  label={FieldProps.PACKAGING_TOTAL_QUANTITY_PLACEHOLDER}
                  placeholderstring={
                    FieldProps.PACKAGING_TOTAL_QUANTITY_PLACEHOLDER
                  }
                  disabled={isDisabled ? true : false}
                  onFocus={() => {
                    fetchData({
                      'input-field': RawMaterialProps.QUANTITY,
                    });
                  }}
                />
              </div>
              <div className={styles.smallFieldContainer}>
                <COSelect
                  key={`${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_UNIT}`}
                  name={`${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_UNIT}`}
                  label={FieldProps.INGREDIENT_UNIT_PLACEHOLDER}
                  placeholderstring={FieldProps.INGREDIENT_UNIT_PLACEHOLDER}
                  options={unitOptions}
                  disabled={isDisabled ? true : false}
                />
              </div>
              <h3>
                {ButtonLabels.ROOT}
                {newProduct?.netWeight} {newProduct?.netWeightUnit}
              </h3>
              {!isDisabled && (
                <div
                  className={styles.addIconContainer}
                  onClick={() =>
                    handleAddOrigins(
                      formikProps,
                      index,
                      RawMaterialProps.INGREDIENT,
                      `${RawMaterialProps.INGREDIENTS}-${RawMaterialProps.ADD_ORIGIN_BUTTON}-${index}`
                    )
                  }>
                  <Tooltip title={'Add origin'}>
                    <Typography.Paragraph>
                      <AddIcon />
                    </Typography.Paragraph>
                  </Tooltip>
                </div>
              )}
              {formikProps.values.ingredients.length > 1 && !isDisabled && (
                <div
                  className={styles.deleteButton}
                  onClick={() => arrayHelpers.remove(index)}>
                  <DeleteButton
                    className={clsx(
                      hasErrorAndTouched(
                        formikProps.errors.ingredients?.[index],
                        formikProps.touched.ingredients?.[index],
                        styles
                      )
                    )}
                  />
                </div>
              )}
            </div>
            {/* multiple origin fields */}
            {(formikProps.values.ingredients[index].origins || []).map(
              (_, originIndex) => (
                <IngredientOriginFields
                  key={`${RawMaterialProps.ORIGINS}-${originIndex}`}
                  index={index}
                  originIndex={originIndex}
                  isDisabled={isDisabled}
                  formikProps={formikProps}
                  fetchData={fetchData}
                  showLocationModal={showLocationModal}
                  handleRemoveOrigins={handleRemoveOrigins}
                />
              )
            )}
            {(formikProps.values.ingredients[index]?.origins ?? []).length >=
              1 &&
              !isDisabled && (
                <AddOriginButton
                  isDisabled={isDisabled}
                  formikProps={formikProps}
                  index={index}
                  handleAddOrigins={handleAddOrigins}
                />
              )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default IngredientList;
